import { useState, useEffect } from 'react';
import { useQuery, useLazyQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';

const LIST_CASES_WITH_DOCUMENTS = gql`
  query ListCasesWithDocuments {
    listMyCases {
      id
      caseNumber
    }
  }
`;

const LIST_DOCUMENTS_BY_CASE = gql`
  query ListDocumentsByCase($caseId: ID!) {
    listDocumentsByCase(caseId: $caseId) {
      id
      fileName
      fileType
      fileSize
      description
      createdAt
      uploadedBy
    }
  }
`;

interface CaseItem {
  id: string;
  caseNumber: string;
}

interface Document {
  id: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  description?: string;
  createdAt: string;
  uploadedBy?: string;
}

interface ListMyCasesData {
  listMyCases: CaseItem[];
}

interface ListDocumentsByCaseData {
  listDocumentsByCase: Document[];
}

export function DocumentsWidget() {
  const navigate = useNavigate();
  const [recentDocuments, setRecentDocuments] = useState<any[]>([]);
  const { data, loading, error } = useQuery<ListMyCasesData>(LIST_CASES_WITH_DOCUMENTS, {
    fetchPolicy: 'cache-and-network',
  });
  const [getDocuments] = useLazyQuery<ListDocumentsByCaseData>(LIST_DOCUMENTS_BY_CASE);

  const cases = data?.listMyCases || [];

  // Fetch documents for all cases
  useEffect(() => {
    const fetchAllDocuments = async () => {
      if (cases.length === 0) return;
      
      const documentsPromises = cases.map(async (caseItem: any) => {
        try {
          const { data } = await getDocuments({
            variables: { caseId: caseItem.id },
          });
          
          const docs = data?.listDocumentsByCase || [];
          return docs.map((doc: any) => ({
            ...doc,
            caseNumber: caseItem.caseNumber,
          }));
        } catch (error) {
          console.error(`Error fetching documents for case ${caseItem.id}:`, error);
          return [];
        }
      });

      const documentsArrays = await Promise.all(documentsPromises);
      const flattenedDocuments = documentsArrays.flat();
      
      // Sort by date and take the 5 most recent
      const sorted = flattenedDocuments.sort((a: any, b: any) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      
      setRecentDocuments(sorted.slice(0, 5));
    };

    fetchAllDocuments();
  }, [cases, getDocuments]);

  const getFileIcon = (fileType: string) => {
    const baseClasses = "w-5 h-5";
    if (fileType.includes('pdf')) {
      return (
        <svg className={baseClasses} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      );
    }
    if (fileType.includes('image')) {
      return (
        <svg className={baseClasses} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    }
    if (fileType.includes('word') || fileType.includes('doc')) {
      return (
        <svg className={baseClasses} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
    }
    if (fileType.includes('excel') || fileType.includes('sheet')) {
      return (
        <svg className={baseClasses} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
    }
    return (
      <svg className={baseClasses} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
      </svg>
    );
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recent Documents</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-aalaw-red"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recent Documents</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-red-600 text-sm">Error loading documents</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="inquiry-card">
      <CardHeader>
        <CardTitle>Recent Documents</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="max-h-96 overflow-y-auto space-y-3">
          {recentDocuments.length === 0 ? (
            <p className="text-center text-gray-500 py-4 text-sm">No documents yet</p>
          ) : (
            recentDocuments.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className="text-gray-600">{getFileIcon(doc.fileType)}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{doc.fileName}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-gray-600 font-mono">{doc.caseNumber}</span>
                    <span className="text-xs text-gray-400">•</span>
                    <span className="text-xs text-gray-500">{formatFileSize(doc.fileSize)}</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    {new Date(doc.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <button className="text-aalaw-red hover:text-aalaw-red-dark">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                </button>
              </div>
            ))
          )}
        </div>
        <Button 
          variant="outline" 
          className="w-full mt-3"
          onClick={() => navigate('/documents')}
        >
          View All Documents
        </Button>
      </CardContent>
    </Card>
  );
}
